import { bulkUploadProducts } from '@commenda/api/new-sales-tax';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@commenda/kakashi';
import {
  readFileColumns,
  SingleFileUploader,
  uploadFileToS3,
} from '@commenda/kakashi/src/components/SingleFileUploader';
import { FileBasic } from '@commenda/types/src/file/types';
import { TSetNotification } from '@commenda/types/src/random';
import { LegacyDocumentType } from '@prisma/client';
import { useState } from 'react';
import { KeyedMutator } from 'swr';
import { downloadSalesTaxProductsCSVTemplate } from '../../common/utils/salesTaxCsvTemplate';
import ProductsColumnMappingModal from './products-column-mapping';

interface SalesTaxImportProductsModalProps {
  isProductsModalShown: boolean;
  setIsProductsModalShown: (isProductsModalShown: boolean) => void;
  corporationId: number;
  setNotification: TSetNotification;
  mutate: KeyedMutator<unknown>;
  onSuccess: KeyedMutator<unknown>;
  companyId: number;
}

const SalesTaxImportProductsModal = ({
  isProductsModalShown,
  setIsProductsModalShown,
  corporationId,
  setNotification,
  mutate,
  onSuccess,
  companyId,
}: SalesTaxImportProductsModalProps) => {
  const [file, setFile] = useState<FileBasic | null>(null);
  const [error, setError] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [showProductsMappingUploadModal, setShowProductsMappingUploadModal] =
    useState(false);

  const [csvColumns, setCsvColumns] = useState<string[]>([]);

  const onUploadProducts = async () => {
    setIsProductsModalShown(false);
    setShowProductsMappingUploadModal(true);
  };

  const handleDownloadCSVTemplate = () => {
    downloadSalesTaxProductsCSVTemplate();

    setNotification({
      show: true,
      valid: true,
      title: 'Successfully downloaded sales tax products template',
      message: 'CSV template downloaded successfully',
    });
  };

  const onUploadProductsWithMapping = async (
    mapping: Record<string, string>,
    updateProtectedColumns: boolean
  ) => {
    if (!file) return;

    const columnMapping = Object.entries(mapping).map(
      ([toColumn, fromColumn]) => ({
        fromColumn,
        toColumn,
      })
    );

    setIsSubmitting(true);

    try {
      const res = await bulkUploadProducts(
        { file, corporationId, columnMapping, updateProtectedColumns },
        undefined
      );

      if (!res.success) {
        setNotification({
          show: true,
          valid: false,
          title: 'Error',
          message: res.error,
        });
        return;
      }

      await onSuccess();
    } finally {
      setNotification({
        show: true,
        valid: true,
        title: 'Upload in Progress',
        message: 'Your products are being uploaded...',
      });

      setIsSubmitting(false);
      setShowProductsMappingUploadModal(false);
      setFile(null);
    }
  };

  const onFileUpload = async (
    fl: File,
    cmpId: number,
    token: string | undefined
  ) => {
    try {
      const columns = await readFileColumns(fl);
      setCsvColumns(columns);

      const uploadResult = await uploadFileToS3(fl, cmpId, token);
      if (!uploadResult) {
        throw new Error('Failed to upload file to S3');
      }

      return uploadResult;
    } catch (err) {
      setError('Failed to process CSV file');
      throw err;
    }
  };

  const handleFileUpload = async (fileBasic: FileBasic) => {
    setFile(fileBasic);
    setError('');
  };

  const handleCloseBulkUploadModal = () => {
    setIsProductsModalShown(false);
    setFile(null);
    setError('');
  };

  return (
    <>
      <ProductsColumnMappingModal
        isOpen={showProductsMappingUploadModal}
        onClose={() => setShowProductsMappingUploadModal(false)}
        onUpload={onUploadProductsWithMapping}
        csvColumnsFromUpload={csvColumns}
        fileName={file?.fileName || ''}
        disabled={!file || isSubmitting}
      />

      <Dialog
        open={isProductsModalShown}
        onOpenChange={setIsProductsModalShown}
      >
        <DialogContent
          className="bg-white min-w-[650px] flex flex-col"
          hideCloseIcon
          preventCloseOnOutsideClick
        >
          <DialogHeader className="flex items-start flex-col gap-1">
            <DialogTitle className="flex-start">
              Import Products Data
            </DialogTitle>
            <p className="txt-small-regular text-text-tertiary">
              Upload a CSV or Excel file to import your products.
            </p>
          </DialogHeader>
          <div className="flex flex-col flex-1 overflow-hidden p-6">
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <div className="txt-medium-bold">Upload your file</div>
                <div
                  className="txt-small-bold text-utility-blue-600 cursor-pointer"
                  onClick={handleDownloadCSVTemplate}
                >
                  Download Product Template
                </div>
              </div>

              <SingleFileUploader
                companyId={companyId}
                file={file as FileBasic}
                setFile={setFile}
                error={error}
                setError={setError}
                onFileChange={handleFileUpload}
                onFileUpload={onFileUpload}
                onError={() => {}}
                token={undefined}
                documentType={LegacyDocumentType.Other}
                maxSize={10 * 1024 * 1024} // 10MB
                accept={{
                  'text/csv': ['.csv'],
                  'application/vnd.ms-excel': ['.csv', '.xls'],
                  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
                    ['.xlsx'],
                  'application/csv': ['.csv'],
                }}
                message="Supported file formats: CSV, XLS, XLSX (Max size: 10MB)"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              size="sm"
              onClick={handleCloseBulkUploadModal}
              variant="secondary"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={onUploadProducts}
              disabled={!file || isSubmitting}
              isLoading={isSubmitting}
            >
              Continue to mapping
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SalesTaxImportProductsModal;
